import { GameBoard, PIECES, type RawPiece } from '@blind-kings/game-core';
import { Link } from 'react-router-dom';
import BkButton from '../../Components/ui/BkButton';
import BkPage from '../../Components/ui/BkPage';
import BkPanel from '../../Components/ui/BkPanel';
import JsonLd from '../../Components/JsonLd';
import { HOW_TO_PLAY_JSON_LD } from '../../config/howto';
import { LEGAL, SUPPORT_MAILTO } from '../../config/legal';
import { usePlayModalContext } from '../../contexts/PlayModalContext';
import usePageMeta from '../../hooks/use-page-meta';
import PieceRoster from './PieceRoster';
import RuleDemoBoard, { RULE_DEMO_BOARD_SIZE } from './RuleDemoBoard';
import RuleSection from './RuleSection';
import RulesToc, { type RulesTocItem } from './RulesToc';

const giveBoardIds = (
  row: { value: number; teamIndex?: number; name?: string; id?: number }[],
  y: number,
  { length }: { length: number }
): RawPiece[] =>
  row.map((piece, x) => ({
    id: y * length + x,
    ...piece,
    teamIndex: piece.teamIndex as RawPiece['teamIndex'],
  }));

const FLIP_GAME_BOARD = new GameBoard({
  board: [
    [PIECES.Unturned, PIECES.Unturned, PIECES.Unturned],
    [PIECES.Unturned, PIECES.Unturned, PIECES.Unturned],
    [PIECES.Unturned, PIECES.Unturned, PIECES.Unturned],
  ].map(giveBoardIds),
});

const MOVE_GAME_BOARD = new GameBoard({
  board: [
    [PIECES.Empty, PIECES.Empty, PIECES.Empty],
    [PIECES.Empty, { ...PIECES.Mage, teamIndex: 0 }, PIECES.Empty],
    [PIECES.Empty, PIECES.Empty, PIECES.Empty],
  ].map(giveBoardIds),
});

const ATTACKING_GAME_BASIC = new GameBoard({
  board: [
    [PIECES.Unturned, { ...PIECES.Cavalry, teamIndex: 1 }, PIECES.Unturned],
    [
      { ...PIECES.Mage, teamIndex: 1 },
      { ...PIECES.Mage, teamIndex: 0 },
      { ...PIECES.Hero, teamIndex: 1 },
    ],
    [PIECES.Unturned, { ...PIECES.Peasant, teamIndex: 1 }, PIECES.Unturned],
  ].map(giveBoardIds),
});

const ATTACKING_GAME_PEASANT = new GameBoard({
  board: [
    [PIECES.Unturned, { ...PIECES.Knight, teamIndex: 1 }, PIECES.Unturned],
    [
      { ...PIECES.Peasant, teamIndex: 1 },
      { ...PIECES.Peasant, teamIndex: 0 },
      { ...PIECES.King, teamIndex: 1 },
    ],
    [PIECES.Unturned, { ...PIECES.Assassin, teamIndex: 1 }, PIECES.Unturned],
  ].map(giveBoardIds),
});

const ATTACKING_GAME_KING = new GameBoard({
  board: [
    [PIECES.Unturned, { ...PIECES.Hero, teamIndex: 1 }, PIECES.Unturned],
    [
      { ...PIECES.Peasant, teamIndex: 1 },
      { ...PIECES.King, teamIndex: 0 },
      { ...PIECES.Assassin, teamIndex: 1 },
    ],
    [PIECES.Unturned, { ...PIECES.King, teamIndex: 1 }, PIECES.Unturned],
  ].map(giveBoardIds),
});

const ATTACKING_GAME_ASSASSIN = new GameBoard({
  board: [
    [{ ...PIECES.Peasant, teamIndex: 1 }, PIECES.Unturned, PIECES.Unturned],
    [
      { ...PIECES.Assassin, teamIndex: 0 },
      PIECES.Unturned,
      { ...PIECES.Cavalry, teamIndex: 1 },
    ],
    [{ ...PIECES.Hero, teamIndex: 1 }, PIECES.Unturned, PIECES.Unturned],
    [PIECES.Empty, PIECES.Unturned, PIECES.Unturned],
    [{ ...PIECES.King, teamIndex: 1 }, PIECES.Unturned, PIECES.Unturned],
  ].map(giveBoardIds),
});

const TOC_ITEMS: RulesTocItem[] = [
  { id: 'quickstart', label: 'Quickstart' },
  { id: 'pieces', label: 'Pieces' },
  { id: 'reveal', label: 'Reveal' },
  { id: 'move', label: 'Move' },
  { id: 'attack', label: 'Attack' },
  { id: 'specials', label: 'Specials' },
  { id: 'winning', label: 'Winning' },
  { id: 'fair-play', label: 'Fair Play' },
];

function SpecialCard({
  title,
  bullets,
  gameBoard,
  selectedPiece,
  size = RULE_DEMO_BOARD_SIZE,
  aspectRatio = 1,
}: {
  title: string;
  bullets: string[];
  gameBoard: GameBoard;
  selectedPiece: RawPiece;
  size?: number;
  aspectRatio?: number;
}) {
  return (
    <BkPanel className="space-y-4">
      <h3 className="text-base font-semibold uppercase tracking-wide text-bk-gold sm:text-lg">
        {title}
      </h3>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
        <RuleDemoBoard
          gameBoard={gameBoard}
          seedPiece={selectedPiece}
          size={size}
          aspectRatio={aspectRatio}
        />
        <ul className="list-disc space-y-2 pl-5 text-sm text-bk-text/90 sm:flex-1 sm:text-base">
          {bullets.map((bullet) => (
            <li key={bullet}>{bullet}</li>
          ))}
        </ul>
      </div>
    </BkPanel>
  );
}

export default function Rules() {
  const { openPlayModal } = usePlayModalContext();
  usePageMeta({
    title: 'Rules — How to Play',
    description:
      'Learn how to play Blind Kings: reveal hidden pieces, move, attack, and win this hidden-piece strategy game.',
    path: '/rules',
  });

  return (
    <BkPage>
      <JsonLd data={HOW_TO_PLAY_JSON_LD} />
      <h1 className="mb-2 text-2xl font-bold uppercase tracking-wider text-bk-gold md:text-3xl">
        Rules
      </h1>
      <p className="mb-4 max-w-2xl text-sm text-bk-muted sm:text-base">
        A quick guide to Blind Kings. Boards show examples of each rule.
      </p>

      <RulesToc items={TOC_ITEMS} />

      <div className="space-y-12 pb-10">
        <section id="quickstart" className="scroll-mt-24 space-y-4">
          <h2 className="border-b border-bk-gold-dim/40 pb-2 text-xl font-bold uppercase tracking-wide text-bk-text sm:text-2xl md:text-3xl">
            Quickstart
          </h2>
          <BkPanel className="space-y-3 text-sm text-bk-text/90 sm:text-base">
            <p>
              You are a Blind King. Both armies start face-down and mixed together — you
              won&apos;t know which pieces are yours until you flip them.
            </p>
            <p>
              On your turn, do one thing:{' '}
              <span className="font-semibold text-bk-gold">Reveal</span>,{' '}
              <span className="font-semibold text-bk-gold">Move</span>, or{' '}
              <span className="font-semibold text-bk-gold">Attack</span>.
            </p>
            <p>Win by clearing your rival&apos;s pieces — or by leaving them with nothing left to do.</p>
          </BkPanel>
          <div className="flex flex-wrap gap-3 text-xs sm:text-sm">
            <span className="inline-flex items-center gap-2 border border-bk-border bg-bk-panel px-3 py-1.5 text-bk-muted">
              <span className="h-2.5 w-2.5 rounded-full bg-[#3d7ea6]" aria-hidden />
              Your pieces
            </span>
            <span className="inline-flex items-center gap-2 border border-bk-border bg-bk-panel px-3 py-1.5 text-bk-muted">
              <span className="h-2.5 w-2.5 rounded-full bg-[#c43c3c]" aria-hidden />
              Rival pieces
            </span>
            <span className="inline-flex items-center gap-2 border border-bk-border bg-bk-panel px-3 py-1.5 text-bk-muted">
              <span className="h-2.5 w-2.5 rounded-full bg-[#2f9e44]" aria-hidden />
              Face-down
            </span>
          </div>
        </section>

        <section id="pieces" className="scroll-mt-24 space-y-4">
          <h2 className="border-b border-bk-gold-dim/40 pb-2 text-xl font-bold uppercase tracking-wide text-bk-text sm:text-2xl md:text-3xl">
            Pieces
          </h2>
          <PieceRoster />
        </section>

        <RuleSection
          id="reveal"
          title="Reveal"
          bullets={[
            'Flip any face-down piece on the board.',
            'The first flip always belongs to the starting player.',
            'After that, flips can go to either player.',
          ]}
          board={{
            gameBoard: FLIP_GAME_BOARD,
            selectedPiece: FLIP_GAME_BOARD.board[1][1] as RawPiece,
            prompt: 'Face-down pieces can be revealed on your turn.',
          }}
        />

        <RuleSection
          id="move"
          title="Move"
          bullets={[
            'Move one of your revealed pieces into a neighboring empty space.',
            'Neighbors are up, down, left, or right — not diagonal.',
          ]}
          board={{
            gameBoard: MOVE_GAME_BOARD,
            selectedPiece: MOVE_GAME_BOARD.board[1][1] as RawPiece,
            prompt: 'Highlighted spaces show where this Mage can move.',
          }}
        />

        <RuleSection
          id="attack"
          title="Attack"
          bullets={[
            'Attack a neighboring rival if your CP is equal or higher.',
            'King, Peasant, and Assassin follow their own rules below.',
          ]}
          board={{
            gameBoard: ATTACKING_GAME_BASIC,
            selectedPiece: ATTACKING_GAME_BASIC.board[1][1] as RawPiece,
            prompt: 'Highlighted rivals show who this Mage can attack.',
          }}
        />

        <section id="specials" className="scroll-mt-24 space-y-6">
          <h2 className="border-b border-bk-gold-dim/40 pb-2 text-xl font-bold uppercase tracking-wide text-bk-text sm:text-2xl md:text-3xl">
            Special Pieces
          </h2>

          <SpecialCard
            title="King"
            bullets={[
              'The King can attack any neighboring rival.',
              'Peasants are the exception — they beat Kings.',
            ]}
            gameBoard={ATTACKING_GAME_KING}
            selectedPiece={ATTACKING_GAME_KING.board[1][1] as RawPiece}
          />

          <SpecialCard
            title="Peasant"
            bullets={[
              'Peasants can take down a King.',
              'Everything else beats a Peasant.',
            ]}
            gameBoard={ATTACKING_GAME_PEASANT}
            selectedPiece={ATTACKING_GAME_PEASANT.board[1][1] as RawPiece}
          />

          <SpecialCard
            title="Assassin"
            bullets={[
              'Assassins cannot attack neighbors.',
              'They jump over exactly one piece to strike.',
              'After the jump, they can hit any piece — empty spaces past the jump are fine.',
            ]}
            gameBoard={ATTACKING_GAME_ASSASSIN}
            selectedPiece={ATTACKING_GAME_ASSASSIN.board[1][0] as RawPiece}
            aspectRatio={5 / 3}
          />
        </section>

        <section id="winning" className="scroll-mt-24 space-y-4">
          <h2 className="border-b border-bk-gold-dim/40 pb-2 text-xl font-bold uppercase tracking-wide text-bk-text sm:text-2xl md:text-3xl">
            Winning
          </h2>
          <BkPanel className="space-y-4 text-sm text-bk-text/90 sm:text-base">
            <p>You win if:</p>
            <ul className="list-disc space-y-2 pl-5">
              <li>Your rival has no pieces left.</li>
              <li>Your rival has no moves left.</li>
              <li>The game stalls for too long with no reveals or attacks.</li>
            </ul>
            <p>
              If the game stalls out, the player with more pieces wins. Ties go to the
              highest piece still on the board, then to whoever last revealed or attacked.
            </p>
          </BkPanel>
        </section>

        <section id="fair-play" className="scroll-mt-24 space-y-4">
          <h2 className="border-b border-bk-gold-dim/40 pb-2 text-xl font-bold uppercase tracking-wide text-bk-text sm:text-2xl md:text-3xl">
            Fair Play
          </h2>
          <BkPanel className="space-y-3 text-sm text-bk-text/90 sm:text-base">
            <p>
              Don&apos;t keep chasing the same piece just to stall the game. The rules stop
              endless chasing — keep the match moving.
            </p>
          </BkPanel>
        </section>

        <BkPanel raised className="flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-lg font-bold uppercase tracking-wide text-bk-gold sm:text-xl">
              Ready to play?
            </h2>
            <p className="mt-1 text-sm text-bk-muted sm:text-base">
              Jump into a match — or email{' '}
              <a
                className="text-bk-gold underline-offset-2 hover:underline"
                href={SUPPORT_MAILTO}
              >
                {LEGAL.supportEmail}
              </a>{' '}
              if you need help.
            </p>
          </div>
          <BkButton variant="primary" size="lg" onClick={openPlayModal}>
            Play Now
          </BkButton>
        </BkPanel>

        <p className="mt-6 text-sm text-bk-muted">
          <Link
            className="text-bk-gold underline-offset-2 hover:underline"
            to="/terms"
          >
            Terms of Service
          </Link>
          {' · '}
          <Link
            className="text-bk-gold underline-offset-2 hover:underline"
            to="/privacy"
          >
            Privacy Policy
          </Link>
        </p>
      </div>
    </BkPage>
  );
}
